/**
 * 생성기 첫 열림 코치마크 — 화면을 dim 으로 덮고 강조 대상 자리만 뚫은 뒤, 그 위에 말풍선(제목 · 진행 점 · 다음)과
 * 연결선을 띄운다. Figma QUIZ-Build 「AI 원샷 온보딩」(41100:29336) 의 `.Onboarding/코치마크`·`.Onboarding/연결선`.
 *
 * 말풍선 위치는 Radix Popover 가 정한다 — 가상 기준(구멍 위 가장자리)에 위로 붙이고, 화면 가장자리에 걸리면 안으로 밀며,
 * 밀려도 연결선(Popover.Arrow)은 대상 가운데를 계속 가리킨다. dim 구멍은 Radix 밖이라 직접 잰 대상 위치로 그린다.
 * 순수 UI 다 — 무엇을 강조할지(target)와 단계는 호출부가 정한다.
 */
import { useId, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@zep-us/zus/button';
import { Black, White } from '@zep-us/zus/tokens';
import { Popover } from '@/ui_v3';
import { cn } from '@/utils/tailwind.util';
import { AI_GENERATE_DOCK_ONBOARDING_CONNECTOR, AI_GENERATE_DOCK_ONBOARDING_EDGE_PX, AI_GENERATE_DOCK_ONBOARDING_STEPS, type AiGenerateDockOnboardingStep } from '../consts/aiGenerateDockOnboarding.const';
import { useAiGenerateDockOnboardingTargetRect } from '../hooks/useAiGenerateDockOnboardingTargetRect';
import { type AiGenerateDockOnboardingCopy } from '../types/aiGenerateDock.types';
import { type AiGenerateDockOnboardingRect, isLastAiGenerateDockOnboardingStep, toAiGenerateDockOnboardingAnchor, toAiGenerateDockOnboardingHole } from '../utils/aiGenerateDockOnboarding';

/** dim — ZUS `bg/dimmed`(black 24%, #1C1B1F3D). 구멍 둘레 그림자로 그려 화면 전체를 덮는다. */
const DIM_SHADOW = `0 0 0 200vmax ${Black}3D`;

/** 입력창(z-40)·뒤 흐림(z-30) 위, ZUS 드롭다운(500) 아래 — play 코치마크(dim 390 · 말풍선 400)와 같은 층. */
const DIM_Z_CLASS = 'z-[390]';
const CARD_Z_CLASS = 'z-[400]';

/** 말풍선 모서리(16)에 연결선이 붙지 않게 — 화면 가장자리에서 밀려도 선은 둥근 모서리 안쪽에 남는다. */
const CONNECTOR_PADDING_PX = 16;
export type AiGenerateDockOnboardingUIProps = {
  step: AiGenerateDockOnboardingStep;
  /** 강조할 요소 — 아직 그려지지 않았으면 null 이고, 그동안은 아무것도 띄우지 않는다. */
  target: HTMLElement | null;
  copy: AiGenerateDockOnboardingCopy;
  onNext: () => void;
  /** Esc — 입력창보다 먼저 받아 코치마크만 걷는다(입력창은 열린 채 남는다). */
  onEscape: () => void;
};

/** 가상 기준 — Radix 가 위치를 다시 잴 때마다 대상을 새로 읽는다. contextElement 로 대상 크기 변화도 따라간다. */
const useVirtualAnchor = (target: HTMLElement | null, step: AiGenerateDockOnboardingStep) => useMemo(() => ({
  current: target && {
    contextElement: target,
    getBoundingClientRect: () => {
      const {
        top,
        left,
        width,
        height
      } = toAiGenerateDockOnboardingAnchor(toAiGenerateDockOnboardingHole(target.getBoundingClientRect(), step));
      return {
        x: left,
        y: top,
        top,
        left,
        width,
        height,
        right: left + width,
        bottom: top + height,
        toJSON: () => undefined
      } satisfies DOMRect;
    }
  }
}), [target, step]);

/**
 * dim — 구멍 둘레 네 조각이 뒤 클릭을 막고(눌러도 닫지 않는다), 구멍 안은 대상을 그대로 누를 수 있다.
 * 보이는 어둠은 구멍 상자의 큰 그림자 하나로 그린다(모서리 12 · ZUS rounded-large).
 */
const OnboardingDim = ({
  hole
}: {
  hole: AiGenerateDockOnboardingRect;
}) => {
  const right = hole.left + hole.width;
  const bottom = hole.top + hole.height;
  const blockerClass = 'pointer-events-auto absolute';
  return <div aria-hidden data-testid="ai-generate-dock-onboarding-dim" className={cn('pointer-events-none fixed inset-0', DIM_Z_CLASS)} data-sentry-component="OnboardingDim" data-sentry-source-file="AiGenerateDockOnboardingUI.tsx">
      <div className={blockerClass} style={{
      top: 0,
      left: 0,
      right: 0,
      height: hole.top
    }} />
      <div className={blockerClass} style={{
      top: bottom,
      left: 0,
      right: 0,
      bottom: 0
    }} />
      <div className={blockerClass} style={{
      top: hole.top,
      left: 0,
      width: hole.left,
      height: hole.height
    }} />
      <div className={blockerClass} style={{
      top: hole.top,
      left: right,
      right: 0,
      height: hole.height
    }} />
      <div data-testid="ai-generate-dock-onboarding-hole" className="absolute rounded-large" style={{
      ...hole,
      boxShadow: DIM_SHADOW
    }} />
    </div>;
};

/** 진행 점 — 6px 점 사이 6, 지금 단계만 진한 보라(Figma 진행 점: Violet500 · 나머지 Violet200). */
const ProgressDots = ({
  step
}: {
  step: AiGenerateDockOnboardingStep;
}) => <div aria-hidden data-testid="ai-generate-dock-onboarding-dots" className="flex items-center gap-2xs" data-sentry-component="ProgressDots" data-sentry-source-file="AiGenerateDockOnboardingUI.tsx">
    {AI_GENERATE_DOCK_ONBOARDING_STEPS.map(item => <span key={item} data-current={item === step} className={cn('size-[6px] rounded-full', item === step ? 'bg-violet-500' : 'bg-violet-200')} />)}
  </div>;

/** 연결선 — 흰 선(2) 끝에 흰 점(지름 10). Figma `.Onboarding/연결선` 을 세로로 세운 모양 그대로. */
const Connector = () => <svg width={AI_GENERATE_DOCK_ONBOARDING_CONNECTOR.width} height={AI_GENERATE_DOCK_ONBOARDING_CONNECTOR.height} viewBox="0 0 10 48" fill="none" aria-hidden data-testid="ai-generate-dock-onboarding-connector" data-sentry-element="svg" data-sentry-component="Connector" data-sentry-source-file="AiGenerateDockOnboardingUI.tsx">
    <rect x="4" width="2" height="42" fill={White} data-sentry-element="rect" data-sentry-source-file="AiGenerateDockOnboardingUI.tsx" />
    <circle cx="5" cy="43" r="5" fill={White} data-sentry-element="circle" data-sentry-source-file="AiGenerateDockOnboardingUI.tsx" />
  </svg>;
export const AiGenerateDockOnboardingUI = ({
  step,
  target,
  copy,
  onNext,
  onEscape
}: AiGenerateDockOnboardingUIProps) => {
  const titleId = useId();
  const targetRect = useAiGenerateDockOnboardingTargetRect(target);
  const anchorRef = useVirtualAnchor(target, step);
  if (!target || !targetRect || typeof document === 'undefined') return null;
  const isLast = isLastAiGenerateDockOnboardingStep(step);
  return <>
      {createPortal(<OnboardingDim hole={toAiGenerateDockOnboardingHole(targetRect, step)} />, document.body)}
      <Popover.Root open data-sentry-element="unknown" data-sentry-source-file="AiGenerateDockOnboardingUI.tsx">
        <Popover.Anchor virtualRef={anchorRef} data-sentry-element="unknown" data-sentry-source-file="AiGenerateDockOnboardingUI.tsx" />
        <Popover.Portal data-sentry-element="unknown" data-sentry-source-file="AiGenerateDockOnboardingUI.tsx">
          <Popover.Content side="top" align="center" sideOffset={0} collisionPadding={AI_GENERATE_DOCK_ONBOARDING_EDGE_PX} arrowPadding={CONNECTOR_PADDING_PX} aria-labelledby={titleId} data-testid="ai-generate-dock-onboarding" data-step={step}
        // 입력칸 포커스를 뺏지 않는다 — 1단계가 바로 「여기에 적어 보세요」다.
        onOpenAutoFocus={event => event.preventDefault()} onCloseAutoFocus={event => event.preventDefault()}
        // 바깥(입력칸 포함)을 눌러도 닫지 않는다 — 끝내는 건 호출부가 정한 행동뿐이다.
        onInteractOutside={event => event.preventDefault()} onEscapeKeyDown={event => {
          // Radix 는 document capture 단계에서 받는다 — 여기서 멈춰 입력창의 Esc(닫기)까지 가지 않게 한다.
          event.stopPropagation();
          event.preventDefault();
          onEscape();
        }} className={cn('relative w-[336px] max-w-[calc(100vw-32px)] flex-col items-stretch gap-sm rounded-md bg-default p-md shadow-md', CARD_Z_CLASS)} data-sentry-element="unknown" data-sentry-source-file="AiGenerateDockOnboardingUI.tsx">
            <div>
              <p id={titleId} aria-live="polite" data-testid="ai-generate-dock-onboarding-title" className="text-body-lg-bold text-default">
                {copy.titles[step]}
              </p>
              <div className="flex items-center justify-between">
                <ProgressDots step={step} data-sentry-element="ProgressDots" data-sentry-source-file="AiGenerateDockOnboardingUI.tsx" />
                <Button variant="contained" intent="strong" size="sm" data-testid="ai-generate-dock-onboarding-next" onClick={onNext} data-sentry-element="Button" data-sentry-source-file="AiGenerateDockOnboardingUI.tsx">
                  {isLast ? copy.start : copy.next}
                </Button>
              </div>
              <Popover.Arrow asChild width={AI_GENERATE_DOCK_ONBOARDING_CONNECTOR.width} height={AI_GENERATE_DOCK_ONBOARDING_CONNECTOR.height} data-sentry-element="unknown" data-sentry-source-file="AiGenerateDockOnboardingUI.tsx">
                <Connector data-sentry-element="Connector" data-sentry-source-file="AiGenerateDockOnboardingUI.tsx" />
              </Popover.Arrow>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </>;
};